import React from 'react';
import PropTypes from 'prop-types';
import {duckify, singleDuck, filterDuck, dependentDuck} from '../../../utils/duckify';
import AchInput from './AchInput';
import CreditCardInput from './CreditCardInput';
import StripeBankDebitInput, { STRIPE_ACSS_DEBIT, STRIPE_US_BANK_DEBIT } from './StripeBankDebitInput';
import { paymentProfiles, users, customers, billingConfigs } from "../../../ducks";
import PaymentProfileInput from "./PaymentProfileInput";
import {titleize} from "../../../utils/strings";
import {CheckInput, CustomPaymentInput} from "components/payments/paymentInputs/ManualPaymentInput";
import OnlinePaymentInput from './OnlinePaymentInput'
import FontAwesome from '../../../utils/FA';

import '../styles/payments.scss';

import { ensureKeyed } from 'utils';
import FALabel from '../../shared/FALabel';

export const paymentTypes = {
  CREDIT_CARD: 'credit card',
  ACH: 'electronic check',
  PAYMENT_PROFILE: 'payment profile',
  CHECK: 'check',
  CUSTOM: 'custom',
};

export const ALL_PAYMENT_METHODS = [
  paymentTypes.CREDIT_CARD,
  paymentTypes.ACH,
  paymentTypes.CHECK,
  paymentTypes.CUSTOM,
];

export const BILLING_PAYMENT_METHODS = [
  paymentTypes.ACH,
  paymentTypes.CREDIT_CARD,
];

export const paymentMethodDisplayName = (customer, method) => {
  const {
    payment_config: {
      payment_methods: {
        [method]: {
          display_name = titleize(method)
        } = {}
      } = {}
    } = {}
  } = customer;

  return display_name;
};

export const submitButtonText = (customer, method) => {
  const {
    payment_config: {
      payment_methods: {
        [method]: {
          submit_button_text = `Pay by ${paymentMethodDisplayName(customer, method)}`
        } = {}
      } = {}
    } = {}
  } = customer;

  // Default here in case this gets set as empty string
  return submit_button_text || `Pay by ${paymentMethodDisplayName(customer, method)}`;
};

const CARD_AND_ACH = [paymentTypes.CREDIT_CARD, paymentTypes.ACH];

class PaymentSelector extends React.Component {
  static propTypes = {
    paymentMethods: PropTypes.arrayOf(PropTypes.oneOf(ALL_PAYMENT_METHODS)), // Enabled payment methods
    beforeSubmit: PropTypes.func, // Callback before payment info is submitted.  () => Promise()
    onSubmit: PropTypes.func, // Callback to submit payment info.  ({payment_method, token, account_id, savePaymentProfile, paymentDetails}) => Promise({order, payment_profile})
    afterSubmit: PropTypes.func, // Callback after payment info is submitted.  ({order, payment_profile}) => Promise()

    buttonText: PropTypes.func, // Function to determine button text.  (payment_method) => `Pay By ${titleize(payment_method)}`
    submittingText: PropTypes.func, // Function to determine what text to display when submitting.  (payment_method) => `Submitting ${titleize(payment_method)} Info...`
    paymentMethodDisplayName: PropTypes.func,

    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Optional number if creating an order

    beforeChange: PropTypes.func, // Callback before changing payment methods (apply convenience fees etc).  (payment_method) => Promise()

    selectedPaymentMethod: PropTypes.oneOf([...ALL_PAYMENT_METHODS, paymentTypes.PAYMENT_PROFILE]),

    selectedPaymentProfileId: PropTypes.string,

    convenienceFee: PropTypes.number, // Percentage to be charged as a convenience fee

    showSaveProfileOption: PropTypes.bool, // Whether or not to enable the "save payment profile" checkbox.  If false, profiles will be saved without opt-out

    // From ducks:
    paymentProfiles: PropTypes.arrayOf(PropTypes.object),

    disabled:  PropTypes.bool, //whether or not to disable submit

    payNow: PropTypes.bool, // whether this is a "Pay Now" form

    showNewPaymentOption: PropTypes.bool // optionally disable "Enter New Payment Details"; defaults to true
  };

  static defaultProps = {
    beforeSubmit: () => Promise.resolve(),
    afterSubmit: ({payment_profile, order}) => Promise.resolve({payment_profile, order}),
    buttonText: submitButtonText,
    submittingText: (customer, paymentMethod) => `Submitting ${paymentMethodDisplayName(customer, paymentMethod)} Info...`,
    paymentMethods: [paymentTypes.CREDIT_CARD, paymentTypes.ACH],
    paymentMethodDisplayName,
    beforeChange: () => Promise.resolve(),
    selectedPaymentProfileId: null,
    payNow: false,
    showNewPaymentOption: true
  };

  constructor(){
    super();

    this.state = {
      selectedPaymentMethod: null,
      selectedPaymentProfileId: null,
      submitting: false,
      paymentDetails: {},
      disabled: false
    };
  }

  componentDidMount() {
    const { customer, disabled, selectedPaymentMethod, selectedPaymentProfileId, paymentMethods } = this.props;

    // TODO clean this up.  Ideally either always use state or add a redux reducer for it.  Need to get this out the door relatively fast...
    if (selectedPaymentMethod && paymentMethods.includes(selectedPaymentMethod)) {
      this.setState({ selectedPaymentMethod });
    }

    if (selectedPaymentMethod === paymentTypes.PAYMENT_PROFILE) {
      this.setState({
        selectedPaymentMethod,
        selectedPaymentProfileId
      });
    }

    this.setState({ disabled });
  }

  componentDidUpdate(prevProps, prevState) {
    const { disabled: propDisabled, paymentMethods } = this.props;
    const { disabled: stateDisabled } = this.state;

    if (propDisabled !== stateDisabled) {
      this.setState({ disabled: propDisabled });
    }

    // If the paymentMethods property changes such that any old method is not included
    // in the new methods, reset the selectedPaymentMethod state
    if (paymentMethods.length !== prevProps.paymentMethods.length
      || prevProps.paymentMethods.some(m => !paymentMethods.includes(m))
    ) {
      this.setState({ selectedPaymentMethod: null });
    }
  }

  paymentDetailsChanged(paymentDetails) {
    this.setState({paymentDetails});
  }

  /**
   * With multiple PaymentProfiles, we need to determine which PaymentProfile is currently selected. This
   * returns the currently selected PaymentProfile based on:
   * - User selected profile;
   * - or default to autopay profile;
   * - or default to first profile.
   * @return {*}
   */
  selectedPaymentProfile() {
    const { paymentProfiles } = this.props;
    const { selectedPaymentProfileId } = this.state;

    let paymentProfile;
    if (selectedPaymentProfileId) {
      paymentProfile = paymentProfiles.find(p => p.id === selectedPaymentProfileId);
    }
    if (!paymentProfile) {
      paymentProfile = paymentProfiles.find(p => p.autopay) || paymentProfiles[0];
    }
    return paymentProfile;
  }

  validPaymentMethod() {
    const { paymentMethods } = this.props;
    const { selectedPaymentMethod } = this.state;

    if (selectedPaymentMethod === paymentTypes.PAYMENT_PROFILE && !this.selectedPaymentProfile()) {
      return false;
    }

    return paymentMethods.includes(selectedPaymentMethod);
  }

  renderPaymentMethod(paymentMethod) {
    const {customer, paymentProfiles} = this.props;

    // Don't pass duckMeta down to child props, or they get confused if they're duckified.
    const {duckMeta, allDucksReady, billingConfig, ...otherProps} = this.props;

    const {paymentDetails, disabled} = this.state;

    const paymentProps = {
      ...otherProps,
      billingConfig,
      disabled,
      paymentDetails,
      paymentDetailsChanged: this.paymentDetailsChanged.bind(this)
    };

    const currency = paymentProps.customer.currency;

    if ((!paymentMethod || paymentMethod === paymentTypes.PAYMENT_PROFILE) && paymentProfiles.some(x => x)) {
      return <PaymentProfileInput paymentProfile={this.selectedPaymentProfile()} {...paymentProps} />;
    }

    switch (paymentMethod) {
      case (paymentTypes.CREDIT_CARD):
        switch (paymentProps.customer.cc_payment_gateway) {
          case ('StripeCustomGateway'):
            return <OnlinePaymentInput
              paymentMethod={paymentMethod}
              selectPaymentMethod={this.selectPaymentMethod}
              {...paymentProps}
            />
          default:
            return <CreditCardInput {...paymentProps} />;
        }
      case (paymentTypes.ACH):
        switch (paymentProps.customer.ach_payment_gateway) {
          case ('StripeCustomGateway'):
            return <OnlinePaymentInput
              paymentMethod={paymentMethod}
              selectPaymentMethod={this.selectPaymentMethod}
              {...paymentProps}
            />
          default:
            return <AchInput {...paymentProps} />;
        }
      case (paymentTypes.CHECK):
        return <CheckInput {...paymentProps} />;
      case (paymentTypes.CUSTOM):
        return <CustomPaymentInput {...paymentProps} />;
      default:
        return (
          <div className='alert alert-danger'>
            {`Unknown Payment Method: ${paymentMethod}`}
          </div>
        );
    }
  }

  renderPaymentProfile(profile) {
    const { selectedPaymentMethod } = this.state;
    const checked = (!selectedPaymentMethod || selectedPaymentMethod === paymentTypes.PAYMENT_PROFILE)
      && profile.id === this.selectedPaymentProfile().id;
    const { payment_verification: paymentVerification } = profile;

    return (
      <div>
        <fieldset className={'payment-profile'}>
          <label className='form-label'>
            <input
              type={'radio'}
              checked={checked}
              onClick={() => this.selectPaymentProfile(profile)}
              readOnly
            />
            Saved Account: {profile.name}
            &nbsp;
            {profile.autopay && <span className='text-success'>(AutoPay)</span>}
            {paymentVerification && paymentVerification.status !== 'verified' && (
              <React.Fragment>
                &nbsp;
                <span className='text-warning'>
                  <FALabel icon='exclamation-circle' label=' Verify' iconFirst />
                </span>
              </React.Fragment>
            )}
          </label>
        </fieldset>
      </div>
    );
  }

  selectPaymentProfile(profile) {
    this.selectPaymentMethod(paymentTypes.PAYMENT_PROFILE, profile.id);
  }

  selectPaymentMethod= (selectedPaymentMethod, selectedPaymentProfileId) => {
    this.setState({
      disabled: true
    });

    const { beforeChange } = this.props;

    beforeChange(selectedPaymentMethod, selectedPaymentProfileId).then(() => {
      this.setState({
        selectedPaymentMethod,
        selectedPaymentProfileId,
        disabled: false
      });
    });
  }

  renderPaymentMethodSelector(showPaymentMethod) {
    const {
      customer,
      paymentMethods
    } = this.props;
    let filteredPaymentMethods = [...paymentMethods]

    // Replacing old cc and check with new stripe payment element
    if (!global.PAYMENTS_TEST_MODE && (customer.cc_payment_gateway === 'StripeCustomGateway') && CARD_AND_ACH.some(method =>
      customer.supported_payment_methods.includes(method))) {
      filteredPaymentMethods = filteredPaymentMethods.filter(p => p !== paymentTypes.CREDIT_CARD && p !== paymentTypes.ACH)
    }

    const isStripeCustom = customer.cc_payment_gateway === 'StripeCustomGateway'
    const usesOnlinePayment = !global.PAYMENTS_TEST_MODE && isStripeCustom && CARD_AND_ACH.some(method =>
      paymentMethods.includes(method)
    )

    return (filteredPaymentMethods.length + (usesOnlinePayment ? 1 : 0) > 1 ) && [
      <h2>Payment Information</h2>,
      <div className={'payment-method-selector'}>
        <ul className={'payment-method-list'}>
          {
            usesOnlinePayment && (
              <li className={`payment-method-selection ${(showPaymentMethod === paymentTypes.CREDIT_CARD || showPaymentMethod === paymentTypes.ACH) ? 'active' : ''}`}>
                <a onClick={() => this.selectPaymentMethod(paymentTypes.CREDIT_CARD, null)}>
                  Online Payment
                </a>
              </li>
            )
          }
          {
            filteredPaymentMethods.map((method) => {
              return (<li className={`payment-method-selection ${showPaymentMethod === method && 'active'}`}>
                <a onClick={() => this.selectPaymentMethod(method, null)}>
                  {paymentMethodDisplayName(customer, method)}
                </a>
              </li>)
            }
            ).map(ensureKeyed())
          }
        </ul>
      </div>
    ].map(ensureKeyed());
  }

  render() {
    const {
      allDucksReady,
      customer,
      paymentProfiles,
      paymentMethods,
      redirectingToUrl,
      showNewPaymentOption
    } = this.props;
    const {selectedPaymentMethod} = this.state;
    const sortedPaymentMethods = [...paymentMethods].sort((a, b) =>
      (ALL_PAYMENT_METHODS.includes(a) ? ALL_PAYMENT_METHODS.indexOf(a) : Infinity) -
      (ALL_PAYMENT_METHODS.includes(b) ? ALL_PAYMENT_METHODS.indexOf(b) : Infinity)
    );

    const paymentProfilesExist = paymentProfiles.some(x=>x);

    let showPaymentMethod;
    if(this.validPaymentMethod(selectedPaymentMethod)) {
      showPaymentMethod = selectedPaymentMethod
    }
    else if(!paymentProfilesExist) {
      showPaymentMethod = sortedPaymentMethods[0];
    }

    if (redirectingToUrl) {
      return <div className={'payment-selector post-payment-redirect alert alert-info'}>
        <FontAwesome name={'spinner'} spin nbsp/>
        Your payment is being submitted.  You will be redirected shortly...
      </div>;
    }

    if (!allDucksReady) {
      // prevent content flash
      return <div />;
    }

    if (!paymentMethods.some(x=>x)) {
      return <div className={'alert alert-danger'}>
        Error: No supported payment methods are configured.  Please contact {customer.name} support for more info.
      </div>;
    }

    return <div className={'payment-selector'}>
      <section>
        {paymentProfilesExist &&
          paymentProfiles
            .map(this.renderPaymentProfile.bind(this))
            .map(ensureKeyed())
        }

        {showNewPaymentOption && paymentProfilesExist && paymentMethods.some(x=>x) && (
          <fieldset className='enter-payment-details'>
            <label >
              <input
                type='radio'
                checked={!!selectedPaymentMethod && selectedPaymentMethod !== paymentTypes.PAYMENT_PROFILE}
                onClick={() => this.selectPaymentMethod(sortedPaymentMethods[0], null)}
                readOnly
              />
              Enter New Payment Details
            </label>
          </fieldset>
        )}
      </section>

      {!!showPaymentMethod && (showPaymentMethod !== paymentTypes.PAYMENT_PROFILE) && this.renderPaymentMethodSelector(showPaymentMethod)}

      {this.renderPaymentMethod(showPaymentMethod)}
    </div>
  }
}

const mapDucksToProps = () => {
  const user = singleDuck(users, 'current');
  const billingConfig = singleDuck(billingConfigs, 'current');

  return {
    billingConfig,
    user,
    customer: singleDuck(customers, 'current'),
    paymentProfiles: dependentDuck(user, ({value: currentUser}) =>
      filterDuck(paymentProfiles, {user_id: currentUser.id})
    )
  };
};

const mapStateToProps = (state, ownProps) => ({
  // We only care about payment profiles that match the allowed payment methods.
  paymentProfiles: ownProps.duckMeta.paymentProfiles.ready ? ownProps.paymentProfiles.filter(
    (profile) => (ownProps.paymentMethods || ALL_PAYMENT_METHODS).some(m => m === profile.payment_method)
  ) : [],
  redirectingToUrl: state.orders.redirectingToUrl
});

export default duckify(mapDucksToProps, mapStateToProps)(PaymentSelector);

export const paymentInputPropTypes = PaymentSelector.propTypes;
export const paymentInputDefaultProps = PaymentSelector.propTypes;
export const unduckified = PaymentSelector;
